import Editor from "@monaco-editor/react";
import { FileText, Lock } from "lucide-react";
import type { TestPlan } from "@/services/testing";
import { CopyButton } from "./RunResult";

/**
 * True when the plan is an IR plan — its `spec_ts` is a COMPILED view regenerated from the IR on
 * every run. Kept as a small helper for callers that special-case IR plans.
 */
export function isCompiledPreview(plan: TestPlan): boolean {
	const steps = plan.steps as unknown;
	if (!steps || typeof steps !== "object" || Array.isArray(steps)) return false;
	const s = steps as { ir?: unknown; format?: unknown };
	return (typeof s.ir === "object" && s.ir !== null) || s.format === "ir_v1";
}

export function planSpecText(plan: TestPlan): string {
	const steps = plan.steps as unknown;
	if (
		steps &&
		typeof steps === "object" &&
		typeof (steps as { spec_ts?: unknown }).spec_ts === "string"
	) {
		return (steps as { spec_ts: string }).spec_ts;
	}
	return typeof steps === "string" ? steps : JSON.stringify(steps ?? {}, null, 2);
}

/**
 * READ-ONLY preview of the plan's compiled TypeScript.
 *
 * The IR is the source of truth; `spec_ts` is a view recompiled from it on every run. Hand-editing the
 * compiled TS was removed along with the backend's `edited_spec_ts` path — executing raw TypeScript
 * bypasses IR validation / the resolver contract / the intent gate, so it is disabled by design. To
 * change a plan, edit a step inline (mutates the IR) or ask the AI in chat (re-plans the IR); the TS
 * shown here recompiles from the updated plan. This deliberately drops the lock/unlock/auto-save
 * machinery — there is no editable state to manage, so there is no read-only "mode" to toggle.
 */
export function PlanSpecMonacoPreview({ plan }: { plan: TestPlan }) {
	const spec = planSpecText(plan);
	return (
		<div className="overflow-hidden rounded-md border border-border bg-card">
			<div className="flex items-center gap-2 border-b border-border px-2 py-1 text-[11px] text-muted-foreground">
				<FileText className="h-3 w-3" />
				<span className="font-mono">.spec.ts</span>
				<CopyButton getText={() => spec} title="Copy .spec.ts" />
				<span className="inline-flex items-center gap-1 rounded bg-muted/50 px-1 py-0.5">
					<Lock className="h-2.5 w-2.5" />
					preview
				</span>
				<span className="ml-auto font-mono">{spec.length} chars</span>
			</div>
			<div className="border-b border-border bg-muted/20 px-2 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
				Compiled from the plan — a read-only view. To change the test, edit a step inline or ask in
				chat; the TypeScript recompiles from the updated plan.
			</div>
			<div className="h-72">
				<Editor
					height="100%"
					language="typescript"
					value={spec}
					theme="vs-dark"
					options={{
						readOnly: true,
						domReadOnly: true,
						minimap: { enabled: false },
						scrollBeyondLastLine: false,
						fontSize: 12,
						lineNumbers: "on",
						renderLineHighlight: "none",
						wordWrap: "off",
						scrollbar: { vertical: "auto", horizontal: "auto" },
						tabSize: 2,
						folding: true,
					}}
				/>
			</div>
		</div>
	);
}
